/**
 * @fileoverview Checkbox-group template defaults (MOR-1003).
 *
 * A fillable-form template's checkbox placements can mark one option of a
 * single-select group as "Selected by default" (`defaultChecked` in the Map
 * Fields editor). Until MOR-1003 that default existed only as a draw-time
 * fallback deep inside the PDF fill action — it was invisible to the
 * generate wizard's review form and, worse, to conditional-field resolution
 * ("Show only when…"): a controlling question that was defaulted but never
 * explicitly answered evaluated as UNANSWERED, so every dependent field —
 * including dependent boxes' own default ticks — stayed suppressed. On
 * heavily-conditioned forms (e.g. the ATO F(ATO)B Appointment or Cessation
 * form) that read as "none of my defaults pull through".
 *
 * These helpers materialise group defaults into the field-values map so a
 * default answer behaves exactly like an explicit answer everywhere:
 * condition evaluation, the wizard review form, the day-one Edit Fields
 * panel, and the PDF fill itself.
 *
 * Dependency-free by design (like fieldConditions.ts) — imported by both
 * the Convex backend (PDF fill) and the frontend wizard.
 */

/**
 * The slice of a `fieldPositions` placement these helpers read. Matches the
 * checkbox-group fields on `precedentTemplates.fieldPositions` rows.
 */
export interface CheckboxGroupPlacement {
  fieldType?: string;
  groupKey?: string;
  optionValue?: string;
  defaultChecked?: boolean;
}

/**
 * Extract `groupKey → default optionValue` from a template's placements.
 *
 * Only checkbox placements that belong to a group and are flagged
 * `defaultChecked` contribute; the first such member with a non-empty
 * option label wins (the editor UI enforces one default per group, this
 * just makes ties deterministic).
 */
export function resolveCheckboxGroupDefaults(
  positions: ReadonlyArray<CheckboxGroupPlacement> | undefined | null,
): Record<string, string> {
  const defaults: Record<string, string> = {};
  if (!positions) return defaults;
  for (const fp of positions) {
    if (fp.fieldType !== "checkbox") continue;
    if (!fp.groupKey || !fp.defaultChecked) continue;
    const option = (fp.optionValue ?? "").trim();
    if (!option) continue;
    if (defaults[fp.groupKey] === undefined) defaults[fp.groupKey] = option;
  }
  return defaults;
}

/**
 * Merge pre-resolved group defaults into a field-values map. A default only
 * fills a group whose current value is missing or blank — an explicit
 * answer (a different option picked in the wizard, a preset value for a
 * field-linked group, a click on the day-one PDF preview) always wins.
 * Blank counts as unanswered to match the fill action's long-standing
 * tick fallback (`isCheckboxTicked` treats "" as "no answer, use default").
 *
 * Returns a new object; the inputs are not mutated.
 */
export function applyGroupDefaultsToValues(
  values: Record<string, string>,
  defaults: Record<string, string>,
): Record<string, string> {
  const merged: Record<string, string> = { ...values };
  for (const [key, option] of Object.entries(defaults)) {
    if ((merged[key] ?? "").trim() === "") merged[key] = option;
  }
  return merged;
}

/**
 * Convenience composition: materialise a template's checkbox-group defaults
 * straight from its placements into a field-values map.
 */
export function applyCheckboxGroupDefaults(
  positions: ReadonlyArray<CheckboxGroupPlacement> | undefined | null,
  values: Record<string, string>,
): Record<string, string> {
  return applyGroupDefaultsToValues(
    values,
    resolveCheckboxGroupDefaults(positions),
  );
}
